import { useCallback, useMemo, useRef } from 'react'
import { Flex, Box } from 'theme-ui'
import { Formik, Form, FormikProps } from 'formik'
import { lazy } from 'yup'
import { Paper, Heading, Text, Button, Loading } from 'components'
import {
  StudentFormSchema,
  UserTypeValidation,
  NetsuiteValidation,
  NonPublicCountrySchema,
  LocationValidation,
  TimeValidation,
  ContactOptionValidation,
  TeachingOrganizationIdValidation,
} from 'components/Form'
import { useAuth } from 'providers'
import { noop } from 'utils'
import { useHandleEscapeKey } from 'hooks'
import { ConfigUtils, FormikOnlyValues, StudentCreateFormValues } from 'types'
import { useUser } from 'hooks/userHooks'
import { hasChanged } from 'utils/helpers'
import {
  STUDENT_INPERSON,
  STUDENT_DC,
  IOS,
  ANDROID,
  STUDENT_CREATE,
} from '../../constants'
import {
  BasicUserSection,
  OrganizationDetailsSection,
  InstructionDetailsSection,
} from '../FormSections'
import { useConfirmation } from 'hooks/useConfirmation'
import getLocalized from 'i18n'

const localized = getLocalized()

type FormValues = Partial<StudentCreateFormValues> & FormikOnlyValues

export type CreateStudentFormProps = {
  isSupport?: boolean
  disabled?: boolean
  pending?: boolean
  isTeacherTraining?: boolean
  responseError?: string
  configUtils: ConfigUtils
  onFormCancel?: () => void
  onFormSubmit: (values: StudentCreateFormValues, fullname?: string) => void
  access?: {
    viewDropdownDate: boolean
  }
}

function getCreateStudentValues({ values }: { values: FormValues }) {
  const formValues = {
    ...values,
  }
  const { __country, __teachingOrg, __teachingOrgId, ...filteredValues } =
    formValues
  if (!filteredValues.netsuiteEmail) {
    delete filteredValues.netsuiteEmail
    delete filteredValues.netsuiteForename
    delete filteredValues.netsuiteId
    delete filteredValues.netsuiteSurname
  }
  if (filteredValues.userType === STUDENT_INPERSON) {
    delete filteredValues.status
  }

  return filteredValues as StudentCreateFormValues
}

export function CreateStudentForm({
  isSupport = false,
  isTeacherTraining = false,
  responseError,
  configUtils,
  disabled = false,
  pending = false,
  onFormSubmit,
  onFormCancel = noop,
  access,
}: CreateStudentFormProps) {
  const { profile } = useAuth()
  const [teacher] = useUser(profile.id)
  const {
    defaultTeachingOrgId: initTeachingOrgId,
    countryCode: initCountryCode = '',
  } = teacher ?? {}

  const generateTestStudentEmail = (email: string) => {
    // add before email's @ the following string: +MMMDD_HH.mm
    const date = new Date()
    const month = date.toLocaleString('default', { month: 'short' })
    const day = date.getDate()
    const hours = date.getHours()
    const minutes = date.getMinutes()
    const emailParts = email.split('@')
    const composedEmail =
      `${emailParts[0]}+${month}${day}_${hours}.${minutes}@${emailParts[1]}`.toLowerCase()
    return composedEmail
  }
  const initialValues = useMemo(
    () =>
      ({
        forename: '',
        surname: '',
        email: isTeacherTraining ? generateTestStudentEmail(profile.email) : '',
        instructionDate: '',
        gender: undefined,
        countryCode: initCountryCode,
        phone: undefined,
        device: undefined,
        isTester: 'no',
        userType: undefined,
        status: 'locked',
        teachingOrgId: initTeachingOrgId,
        locationId: null,
        startTime: null,
        endTime: null,
        contactOption: null,
      } as FormValues),
    [initCountryCode, initTeachingOrgId, isTeacherTraining, profile.email]
  )
  const formikRef = useRef<FormikProps<FormValues>>(null)

  const hasFormChanged = useCallback(() => {
    const form = formikRef.current
    if (form) {
      const values = form.values
      return hasChanged({ values, initialValues })
    } else {
      return false
    }
  }, [formikRef, initialValues])

  const confirmCancel = useConfirmation({
    header: localized.confirmationModal.closeWithoutSaving.header,
    body: localized.confirmationModal.closeWithoutSaving.body,
    shouldConfirm: hasFormChanged,
  })

  const cancel = useCallback(async () => {
    const form = formikRef.current
    if (form) {
      if (await confirmCancel()) {
        form.handleReset()
        onFormCancel()
      }
    }
  }, [formikRef, onFormCancel, confirmCancel])

  useHandleEscapeKey(cancel)
  const handleSubmit = (values: FormValues) => {
    // Create student
    const filteredValues = getCreateStudentValues({ values })
    //if no device selected and it's a public country, set device to ios for backend validation
    if (
      !filteredValues.device &&
      configUtils.isCountryPublic(filteredValues.countryCode)
    ) {
      filteredValues.device = IOS
    }
    if (filteredValues.device !== ANDROID) {
      filteredValues.googlePlayEmail = undefined
    }
    onFormSubmit(filteredValues)
  }
  return (
    <Box sx={{ width: '100%' }} data-cy="student-create-form">
      <Flex variant="userProfile.form">
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          enableReinitialize
          onSubmit={handleSubmit}
          validationSchema={() => {
            return lazy((values: Partial<FormValues>) => {
              let schema = StudentFormSchema.concat(
                TeachingOrganizationIdValidation
              )
              if (
                values.userType === STUDENT_DC ||
                values.userType === STUDENT_INPERSON ||
                configUtils.isCountryBeta(values.countryCode)
              ) {
                schema = schema
                  .concat(LocationValidation)
                  .concat(TimeValidation)
                  .concat(ContactOptionValidation)
              }
              if (
                values.teachingOrgId &&
                configUtils.canLinkToNetSuite(
                  values.countryCode,
                  values.teachingOrgId
                )
              ) {
                schema = schema.concat(NetsuiteValidation)
              }
              if (configUtils.isCountryPublic(values.countryCode)) {
                schema = schema.concat(UserTypeValidation)
              } else {
                schema = schema.concat(NonPublicCountrySchema)
              }
              return schema
            })
          }}>
          {({ touched, handleSubmit }: FormikProps<FormValues>) => {
            return (
              <Form
                onSubmit={handleSubmit}
                autoComplete="off"
                autoCorrect="off">
                <Box sx={{ py: 6 }}>
                  <Heading variant="forms.h1" sx={{ textAlign: 'left' }}>
                    Add a new student
                  </Heading>
                </Box>
                <Box variant={`forms.section.header`}>
                  <Heading variant="forms.h2" sx={{ display: 'inline-block' }}>
                    Student information
                  </Heading>
                </Box>
                <Flex variant={`forms.section.edit`}>
                  <OrganizationDetailsSection
                    isUserUsingNewOrgs={true}
                    pageContext={STUDENT_CREATE}
                    viewMode="edit"
                    isDisabledField={() => disabled}
                    configUtils={configUtils}
                    shouldShowError={(field) =>
                      !!touched[field as keyof FormValues]
                    }
                    startTabIndex={1}
                  />
                  <BasicUserSection
                    viewMode="edit"
                    isDisabledField={() => disabled}
                    isTeacherTraining={isTeacherTraining}
                    configUtils={configUtils}
                    shouldShowError={(field) => !!touched[field]}
                    startTabIndex={11}
                    isSupport={isSupport}
                    pageContext={STUDENT_CREATE}
                  />
                </Flex>
                <Box variant={`forms.section.header`}>
                  <Heading variant="forms.h2" sx={{ display: 'inline-block' }}>
                    Instruction details
                  </Heading>
                </Box>
                <InstructionDetailsSection
                  isUserUsingNewOrgs={true}
                  viewMode="edit"
                  isDisabledField={() => disabled}
                  configUtils={configUtils}
                  shouldShowError={(field) => !!touched[field]}
                  startTabIndex={21}
                  teacher={teacher}
                  isSupport={isSupport}
                  showDropDownPicker={access?.viewDropdownDate === true}
                />

                <Box sx={{ mt: 8 }}>
                  {responseError && (
                    <Paper sx={{ boxShadow: '0 0 0 2px #fbebef' }}>
                      <Flex
                        sx={{
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}>
                        <Text sx={{ fontWeight: 'heading', color: '#f18aa3' }}>
                          {responseError}
                        </Text>
                      </Flex>
                    </Paper>
                  )}
                </Box>

                <Flex
                  variant="forms.buttons.container"
                  sx={{ mt: 45, mb: 165 }}>
                  <Button
                    variant="forms.buttons.primary"
                    data-cy="submit"
                    type="submit"
                    disabled={disabled || !hasFormChanged()}>
                    {pending ? (
                      <Loading />
                    ) : (
                      <>
                        <span>Save</span>
                      </>
                    )}
                  </Button>
                  <Button
                    variant="forms.buttons.secondary"
                    type="button"
                    data-cy="cancel"
                    disabled={disabled}
                    onClick={cancel}>
                    Cancel
                  </Button>
                </Flex>
              </Form>
            )
          }}
        </Formik>
      </Flex>
    </Box>
  )
}
